import React, { useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Modal, Form, Input, InputNumber, Select, Flex, Checkbox, Tooltip, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import { IBill } from '@entities/bill';
import { ICity } from '@entities/city';
import { ICountryItem } from '@entities/country-item';
import { ICountry } from '@entities/country';

import { UnitType } from '@enums/unit-type';
import { ThermometerIcon, LoaderIcon, SumIcon } from '@icons/index';

import './bills.css';

dayjs.extend(utc);

export interface IBillParams {
    billIds: Array<string>;
    boxGroupId?: string;
    setQtyRequired: boolean;
    qty?: number;
    handlingPrice?: number;
    precoolingPrice?: number;
    isTotalPriceManual: boolean;
    totalPrice: number;
    comment?: string;
    setVolumeWeightRequired: boolean;
    volumeWeight?: number;
    setGrossWeightRequired: boolean;
    grossWeight?: number;
    setAwbNumberRequired: boolean;
    awbNumber?: string;
    cargoCarrierId?: string;
    setItemRequired: boolean;
    itemId?: string;
    setCountryRequired: boolean;
    countryId?: string;
}

interface IBillModalParams {
    bill: IBill;
    cities: Array<ICity>;
    items: Array<ICountryItem>;
    countries: Array<ICountry>;
    onSave: (value: IBillParams) => void;
    onCancel: () => void;
}

const BillModal = (props: IBillModalParams) => {
    const { bill, cities, items, countries, onSave, onCancel } = props;

    const { TextArea } = Input;

    const [editBillForm] = Form.useForm();

    const setQtyRequired = Form.useWatch('setQtyRequired', editBillForm);
    const setAwbNumberRequired = Form.useWatch('setAwbNumberRequired', editBillForm);
    const setVolumeWeightRequired = Form.useWatch('setVolumeWeightRequired', editBillForm);
    const setGrossWeightRequired = Form.useWatch('setGrossWeightRequired', editBillForm);
    const setItemRequired = Form.useWatch('setItemRequired', editBillForm);
    const setCountryRequired = Form.useWatch('setCountryRequired', editBillForm);
    const isTotalPriceManual = Form.useWatch('isTotalPriceManual', editBillForm);

    console.log("bill",bill);
    
    useEffect(() => {
        editBillForm.setFieldsValue({
            id: bill?.id,
            countryId: bill?.countryId,
            markingId: bill?.markingId,
            itemId: bill?.itemId,
            boxGroupId: bill?.boxGroupId,
            qty: bill?.qty,
            cityId: bill?.cityId,
            awbNumber: bill?.awbNumber,
            cargoCarrierId: bill?.cargoCarrierId,
            volumeWeight: bill?.volumeWeight,
            grossWeight: bill?.grossWeight,
            handlingPrice: bill?.handlingPrice,
            precoolingPrice: bill?.precoolingPrice,
            totalPrice: bill?.totalPrice,
            isTotalPriceManual: bill?.isTotalPriceManual,
            comment: bill?.comment,
        });
    }, []);

    return (
        <Modal
            width={650}
            title={`Изменить счет для "${bill?.markingCode}"`}
            open={true}
            okText='ОК'
            onOk={() => editBillForm.submit()}
            onCancel={() => {
                editBillForm.resetFields();
                onCancel();
            }}
        >
            <Form
                colon={false}
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                onFinish={onSave}
                form={editBillForm}
                style={{ marginTop: 20 }}
            >
                <Form.Item name='id' hidden={true}>
                    <Input />
                </Form.Item>
                <Form.Item name='boxGroupId' hidden={true}>
                    <Input />
                </Form.Item>

                <Form.Item label='Страна' style={{ marginBottom: 0 }}>
                    <Flex gap='small'>
                        <Form.Item name='setCountryRequired' valuePropName='checked'>
                            <Checkbox />
                        </Form.Item>
                        <Form.Item
                            name='countryId'
                            rules={[{ required: setCountryRequired, message: 'Выберите страну' }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                disabled={!setCountryRequired}
                                showSearch
                                allowClear
                                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                                filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                                options={countries.map((i) => {
                                    return { value: i.id, label: i.name };
                                })}
                            ></Select>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Продукт' style={{ marginBottom: 0 }}>
                    <Flex gap='small'>
                        <Form.Item name='setItemRequired' valuePropName='checked'>
                            <Checkbox />
                        </Form.Item>
                        <Form.Item
                            name='itemId'
                            rules={[{ required: setItemRequired, message: 'Выберите продукт' }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                disabled={!setItemRequired}
                                showSearch
                                allowClear
                                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                                filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                                options={items.map((i) => {
                                    return { value: i.itemId, label: i.itemName };
                                })}
                            ></Select>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Коробки' style={{ marginBottom: 0 }}>
                    <Flex gap='large'>
                        <Flex gap='small'>
                            <Form.Item name='setQtyRequired' valuePropName='checked'>
                                <Checkbox />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space.Compact>
                                    <Form.Item name='qty' style={{ marginBottom: 0 }}>
                                        <InputNumber
                                            disabled={!setQtyRequired}
                                            min={0}
                                            step='1'
                                            onChange={(value: number | null) => {
                                                const qty: number = value || 0;
                                                editBillForm.setFieldValue('qty', qty);
                                            }}
                                        />
                                    </Form.Item>
                                    <Space.Addon>шт</Space.Addon>
                                </Space.Compact>
                            </Form.Item>
                        </Flex>

                        <Form.Item label='Номер AWB' style={{ width: '100%', marginBottom: 0 }}>
                            <Flex gap='small'>
                                <Form.Item name='setAwbNumberRequired' valuePropName='checked'>
                                    <Checkbox />
                                </Form.Item>
                                <Form.Item
                                    name='awbNumber'
                                    rules={[{ required: setAwbNumberRequired, message: 'Укажите AWB' }]}
                                    style={{ width: '100%' }}
                                >
                                    <Input
                                        disabled={!setAwbNumberRequired}
                                        onChange={(data) => {
                                            editBillForm.setFieldValue('awbNumber', data.target.value);
                                        }}
                                    />
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                {bill?.unitType == UnitType.Weight && (
                    <>
                        <Form.Item label='Вес Брутто' style={{ marginBottom: 0 }}>
                            <Flex gap='middle'>
                                <Flex gap='small'>
                                    <Form.Item name='setGrossWeightRequired' valuePropName='checked'>
                                        <Checkbox />
                                    </Form.Item>
                                    <Form.Item
                                        name='grossWeight'
                                        rules={[{ required: setGrossWeightRequired, message: 'Укажите вес Брутто' }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            decimalSeparator=','
                                            min={0}
                                            stringMode
                                            precision={2}
                                            disabled={!setGrossWeightRequired}
                                            onChange={(value: number | null) => {
                                                const grossWeight = value || 0;
                                            }}
                                        />
                                    </Form.Item>
                                </Flex>

                                <Form.Item label='Объемный вес' style={{ marginBottom: 0 }}>
                                    <Flex gap='small'>
                                        <Form.Item name='setVolumeWeightRequired' valuePropName='checked'>
                                            <Checkbox />
                                        </Form.Item>
                                        <Form.Item
                                            name='volumeWeight'
                                            rules={[{ required: setVolumeWeightRequired, message: 'Укажите объемный вес' }]}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                decimalSeparator=','
                                                min={0}
                                                stringMode
                                                precision={2}
                                                disabled={!setVolumeWeightRequired}
                                                onChange={(value: number | null) => {
                                                    const volumeWeight: number = value || 0;

                                                    const handlingPrice: number = editBillForm.getFieldValue('handlingPrice') || 0;
                                                    const precoolingPrice: number = editBillForm.getFieldValue('precoolingPrice') || 0;

                                                    const totalPrice = (+handlingPrice + +precoolingPrice) * +volumeWeight;

                                                    editBillForm.setFieldValue('totalPrice', +totalPrice);
                                                }}
                                            />
                                        </Form.Item>
                                    </Flex>
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    </>
                )}

                <Form.Item label='Тариф' style={{ marginBottom: 0 }}>
                    <Flex gap='middle'>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space.Compact>
                                <Form.Item name='handlingPrice' style={{ marginBottom: 0 }}>
                                    <InputNumber
                                        decimalSeparator=','
                                        disabled={bill?.unitType != UnitType.Weight /* || isTotalPriceManual */}
                                        min={0}
                                        step='0.1'
                                        stringMode
                                        precision={2}
                                        onChange={(value: number | null) => {
                                            const handlingPrice: number | null = value;

                                            const weight: number = editBillForm.getFieldValue('volumeWeight') || 0;

                                            const precoolingPrice: number = editBillForm.getFieldValue('precoolingPrice');

                                            const totalPrice: number = (+(handlingPrice || 0) + +precoolingPrice) * +weight;

                                            editBillForm.setFieldValue('handlingPrice', handlingPrice);
                                            editBillForm.setFieldValue('totalPrice', totalPrice);
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>
                                    <Tooltip title='Хранение' placement='topRight'>
                                        <div>
                                            <LoaderIcon />
                                        </div>
                                    </Tooltip>
                                </Space.Addon>
                            </Space.Compact>
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space.Compact>
                                <Form.Item name='precoolingPrice' style={{ marginBottom: 0 }}>
                                    <InputNumber
                                        decimalSeparator=','
                                        min={0}
                                        step='0.1'
                                        stringMode
                                        precision={2}
                                        onChange={(value: number | null) => {
                                            const precoolingPrice: number = value || 0;

                                            let totalPrice: number = 0;
                                            const weight: number = editBillForm.getFieldValue('volumeWeight') || 0;

                                            const handlingPrice: number = editBillForm.getFieldValue('handlingPrice') || 0;
                                            totalPrice = (+handlingPrice + +precoolingPrice) * +weight;

                                            editBillForm.setFieldValue('precoolingPrice', precoolingPrice);
                                            editBillForm.setFieldValue('totalPrice', totalPrice);
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>
                                    <Tooltip title='Прекулинг' placement='topRight'>
                                        <div>
                                            <ThermometerIcon />
                                        </div>
                                    </Tooltip>
                                </Space.Addon>
                            </Space.Compact>
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Flex gap='small'>
                                <Tooltip title='Задать итоговую сумму вручную'>
                                    <Form.Item name='isTotalPriceManual' valuePropName='checked'>
                                        <Checkbox
                                            onChange={(value: CheckboxChangeEvent) => {
                                                const isManual: boolean = value.target.checked;

                                                if (!isManual) {
                                                    let totalPrice: number = 0;
                                                    const handlingPrice: number = editBillForm.getFieldValue('handlingPrice') || 0;
                                                    const precoolingPrice: number = editBillForm.getFieldValue('precoolingPrice') || 0;

                                                    const weight: number = editBillForm.getFieldValue('volumeWeight') || 0;

                                                    totalPrice = (+handlingPrice + +precoolingPrice) * +weight;

                                                    editBillForm.setFieldValue('totalPrice', totalPrice);
                                                }

                                                editBillForm.setFieldValue('isTotalPriceManual', isManual);
                                            }}
                                        />
                                    </Form.Item>
                                </Tooltip>

                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Space.Compact>
                                        <Form.Item
                                            name='totalPrice'
                                            rules={[{ required: isTotalPriceManual, message: 'Укажите итоговую сумму' }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <InputNumber decimalSeparator=',' min={0} precision={2} disabled={!isTotalPriceManual} />
                                        </Form.Item>
                                        <Space.Addon>
                                            <SumIcon />
                                        </Space.Addon>
                                    </Space.Compact>
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    </Flex>
                </Form.Item>
                <Form.Item
                    label='Комментарий'
                    name='comment'
                    rules={[{ required: isTotalPriceManual, message: 'Укажите причину ввода суммы вручную' }]}
                >
                    <TextArea rows={4} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BillModal;
